import { displayOrDownloadUrlDocument, useDownloadDocument } from "@api";
import type { ICer } from "@bbj-types/candidate/cer";
import { CERStatus } from "@bbj-types/candidate/cerstatus";
import { useI18nContext } from "@root/utils/organisation";
import { type MouseEventHandler, useMemo, useState } from "react";

export const isCerDone = (cer: Pick<ICer, "dates" | "status">) => {
  return cer.dates.endDate <= new Date() && (cer.status === CERStatus.SIGNED || cer.status === CERStatus.SIGNED_PAPER);
};

export const useCerCard = (cer: Pick<ICer, "beneficiary" | "dates" | "document" | "status">) => {
  const { timeZone } = useI18nContext();
  const [{ loading: pdfLoading }, downloadDocument] = useDownloadDocument();
  const [popoverAnchor, setPopoverAnchor] = useState<Element | null>(null);

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
  };

  const handleChipClick: MouseEventHandler<HTMLDivElement> = (event) => {
    setPopoverAnchor(event.currentTarget);
  };

  const cerStartDate = useMemo(() => {
    return cer.dates.startDate.toLocaleString("fr-FR", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
      timeZone,
    });
  }, [cer.dates.startDate]);

  const cerEndDate = useMemo(() => {
    return cer.dates.endDate.toLocaleString("fr-FR", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
      timeZone,
    });
  }, [cer.dates.endDate]);

  const getPdf = async (behaviour: "download" | "display" = "display") => {
    if (cer.document) {
      const result = await downloadDocument(cer.document._id);
      if (result.status === 200) {
        void displayOrDownloadUrlDocument(
          result.data,
          behaviour,
          `Cer ${cer.beneficiary.firstname} ${cer.beneficiary.lastname} ${cerStartDate} ${cerEndDate}`,
          "application/pdf"
        );
      }
    }
  };

  return {
    cerStartDate,
    cerEndDate,
    getPdf,
    pdfLoading,
    isCerDone: isCerDone(cer),
    popoverAnchor,
    handlePopoverClose,
    handleChipClick,
  };
};
